import { GenericQueue } from '../generic/generic-queue';

export enum RequestPriority {
  LOW = 1,
  HIGH = 2,
}

interface RequestItem<T> {
  id?: string;
  dedupeKey?: string;
  payload: (signal?: AbortSignal) => Promise<T>;
  priority: RequestPriority;
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
  canceled?: boolean;
}

class RequestQueue extends GenericQueue<() => Promise<any>> {
  private runningCount = 0;
  private runningTasks = new Map<string, AbortController>();
  private runningTaskMeta = new Map<string, { priority: RequestPriority }>();

  constructor(private maxConcurrency = 4) {
    super();
  }

  enqueueRequest<T>(
    task: (signal?: AbortSignal) => Promise<T>,
    options: {
      priority?: RequestPriority;
      id?: string;
    } = {}
  ): Promise<T> {
    const { priority = RequestPriority.LOW, id } = options;

    let resolveFn!: (value: T) => void;
    let rejectFn!: (reason?: unknown) => void;

    const promise = new Promise<T>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    const item: RequestItem<T> = {
      id,
      payload: task,
      priority,
      resolve: resolveFn,
      reject: rejectFn,
    };

    // PREEMPTION LOGIC
    if (priority === RequestPriority.HIGH) {
      this.preemptLowPriorityTask();
      this.queue.unshift(item as any);
    } else {
      this.queue.push(item as any);
    }

    this.onEnqueue();
    return promise;
  }

  private preemptLowPriorityTask() {
    for (const [id, controller] of this.runningTasks.entries()) {
      // You need to track priority per running task
      const task = this.runningTaskMeta?.get(id);

      if (task?.priority === RequestPriority.LOW) {
        controller.abort();
        this.runningTasks.delete(id);
        this.runningCount--;
        return; // free 1 slot only
      }
    }
  }

  cancelTaskById(id: string) {
    this.queue.forEach((item) => {
      if ((item as any).id === id) {
        (item as any).canceled = true;
      }
    });

    // cancel running task
    const controller = this.runningTasks.get(id);
    if (controller) {
      controller.abort();
      this.runningTasks.delete(id);
    }
  }

  protected onEnqueue() {
    this.runNext();
  }

  private runNext() {
    if (this.runningCount >= this.maxConcurrency) return;

    const item = this.dequeue() as RequestItem<any> | undefined;
    if (!item || item.canceled) return;

    this.runningCount++;

    const controller = new AbortController();

    if (item.id) {
      this.runningTasks.set(item.id, controller);
      this.runningTaskMeta.set(item.id, { priority: item.priority });
    }

    item
      .payload(controller.signal)
      .then(item.resolve)
      .catch(item.reject)
      .finally(() => {
        if (item.id) {
          this.runningTasks.delete(item.id);
          this.runningTaskMeta.delete(item.id);
        }
        this.runningCount--;
        this.runNext();
      });
  }

  private hasPendingWork() {
    return this.queue.length > 0 || this.runningCount > 0;
  }

  cancelAll() {
    if (!this.hasPendingWork()) return;

    console.info('[RequestQueue] cancelAll triggered');

    // cancel queued items
    this.queue.forEach((item: any) => {
      item.canceled = true;

      // resolve silently (prevents UI errors + avoids hanging promises)
      item.resolve?.(undefined);
    });

    this.queue = [];

    // cancel running tasks
    this.runningTasks.forEach((controller, id) => {
      console.info(`[RequestQueue] aborting running task: ${id}`);
      controller.abort();
    });

    this.runningTasks.clear();
    this.runningCount = 0;
  }
}

export const customApiQueue = new RequestQueue(4);
